'use client';

import React, { PropsWithChildren, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getTunnel, PortalName } from './portal-registry';

export type PortalTargetProps = {
  name: PortalName;
  className?: string;
  /**
   * If true, content will be portaled to document.body. Otherwise rendered inline.
   */
  toBody?: boolean;
};

export function PortalTarget({ name, className, toBody }: PortalTargetProps): JSX.Element | null {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const Tunnel = getTunnel(name);
  const content = (
    <div className={className} data-portal-target={name}>
      <Tunnel.Out />
    </div>
  );
  if (toBody) {
    if (!mounted) return null;
    return createPortal(content, document.body);
  }
  return content;
}

export type PortalMountProps = PropsWithChildren<{
  name: PortalName;
  /** If false, children render in place instead of mounting to portal. */
  active?: boolean;
}>;

export function PortalMount({
  name,
  active = true,
  children,
}: PortalMountProps): JSX.Element | null {
  const Tunnel = getTunnel(name);
  if (!active) return <>{children}</>;
  return <Tunnel.In>{children}</Tunnel.In>;
}

// Convenience aliases for the common floating CTA use case
export function FloatingTriggerCTAWrapper({
  children,
  id,
  order,
}: PropsWithChildren<{ id?: string; order?: number }>): JSX.Element | null {
  const dataProps = id ? { 'data-floating-id': id } : {};

  return (
    <PortalMount name="floating-cta">
      <div
        key={id}
        className="self-end pointer-events-auto inline-flex"
        style={order !== undefined ? { order } : undefined}
        {...dataProps}>
        {children}
      </div>
    </PortalMount>
  );
}

export function FloatingTriggerCTAOutlet({
  className,
  toBody,
}: {
  className?: string;
  toBody?: boolean;
}): JSX.Element | null {
  return <PortalTarget name="floating-cta" className={className} toBody={toBody} />;
}
